import { useEffect, useState } from "react";
import { Plus, Trash2, Pencil, FileText, Paperclip, Upload } from "lucide-react";
import { api, uploadFile, fileUrl } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import { money, todayISO } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { DatePicker } from "@/components/DatePicker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

const NONE = "__none__";
const emptyForm = () => ({ po_number: "", job_id: NONE, description: "", amount: "", document_path: "", date: todayISO() });

export default function PurchaseOrders() {
  const { currentId, current } = useCompany();
  const [rows, setRows] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editId, setEditId] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = () => api.get("/purchase-orders").then((r) => setRows(r.data));
  useEffect(() => {
    if (!currentId) return;
    load();
    api.get("/jobs").then((r) => setJobs(r.data));
  }, [currentId]);

  const openNew = () => { setForm(emptyForm()); setEditId(null); setOpen(true); };
  const openEdit = (p) => {
    setForm({
      po_number: p.po_number, job_id: p.job_id || NONE, description: p.description || "",
      amount: p.amount != null ? String(p.amount) : "", document_path: p.document_path || "", date: p.date || todayISO(),
    });
    setEditId(p.id);
    setOpen(true);
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const { path } = await uploadFile(file);
      setForm((f) => ({ ...f, document_path: path }));
      toast.success("PO document uploaded");
    } catch (err) {
      toast.error("Could not upload document");
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (!form.po_number.trim()) return toast.error("PO number is required");
    const payload = {
      po_number: form.po_number.trim(),
      job_id: form.job_id === NONE ? null : form.job_id,
      description: form.description,
      amount: parseFloat(form.amount) || 0,
      document_path: form.document_path || "",
      date: form.date || "",
    };
    try {
      if (editId) await api.put(`/purchase-orders/${editId}`, payload);
      else await api.post("/purchase-orders", payload);
      toast.success(editId ? "PO updated" : "PO added");
      setOpen(false);
      load();
    } catch (e) {
      toast.error("Could not save PO");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this purchase order?")) return;
    await api.delete(`/purchase-orders/${id}`);
    load();
  };

  return (
    <div className="max-w-5xl">
      <div className="flex items-end justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">Purchase Orders</h1>
          <p className="text-slate-500 text-sm mt-1">{current?.name} · record client POs and link them to a job &amp; invoice.</p>
        </div>
        <Button data-testid="add-po-btn" onClick={openNew} className="rounded-md bg-slate-900 hover:bg-slate-800">
          <Plus className="h-4 w-4 mr-2" /> New PO
        </Button>
      </div>

      <Card className="rounded-lg border-slate-200 shadow-sm overflow-hidden p-0">
        {rows.length === 0 ? (
          <div className="p-16 text-center">
            <FileText className="h-8 w-8 mx-auto text-slate-300" />
            <p className="text-slate-400 mt-3 text-sm">No purchase orders yet.</p>
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="po-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-slate-400 border-b border-slate-200">
                <th className="px-5 py-3 font-medium">PO #</th>
                <th className="px-5 py-3 font-medium">Job</th>
                <th className="px-5 py-3 font-medium">Description</th>
                <th className="px-5 py-3 font-medium text-right">Amount</th>
                <th className="px-5 py-3 font-medium">Document</th>
                <th className="px-5 py-3 font-medium text-right"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id} className="border-b border-slate-50 hover:bg-slate-50">
                  <td className="px-5 py-3 font-medium text-slate-900">{p.po_number}</td>
                  <td className="px-5 py-3 text-slate-600">{p.job_code ? `${p.job_code} · ` : ""}{p.job_name || "—"}</td>
                  <td className="px-5 py-3 text-slate-600">{p.description || "—"}</td>
                  <td className="px-5 py-3 text-right tabular-nums">{money(p.amount)}</td>
                  <td className="px-5 py-3">
                    {p.document_path ? (
                      <a href={fileUrl(p.document_path)} target="_blank" rel="noreferrer" data-testid={`view-po-doc-${p.id}`} className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline">
                        <Paperclip className="h-3 w-3" /> View PDF
                      </a>
                    ) : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-5 py-3 text-right whitespace-nowrap">
                    <Button variant="ghost" size="icon" data-testid={`edit-po-${p.id}`} onClick={() => openEdit(p)}>
                      <Pencil className="h-4 w-4 text-slate-500" />
                    </Button>
                    <Button variant="ghost" size="icon" data-testid={`delete-po-${p.id}`} onClick={() => remove(p.id)}>
                      <Trash2 className="h-4 w-4 text-rose-500" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{editId ? "Edit Purchase Order" : "New Purchase Order"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>PO Number</Label>
                <Input data-testid="po-number-input" value={form.po_number} onChange={(e) => setForm({ ...form, po_number: e.target.value })} className="mt-1 rounded-md" placeholder="PO-12345" />
              </div>
              <div>
                <Label>Date</Label>
                <div className="mt-1"><DatePicker testId="po-date-picker" value={form.date} onChange={(d) => setForm({ ...form, date: d })} /></div>
              </div>
            </div>
            <div>
              <Label>Job</Label>
              <Select value={form.job_id} onValueChange={(v) => setForm({ ...form, job_id: v })}>
                <SelectTrigger data-testid="po-job-select" className="mt-1 rounded-md"><SelectValue placeholder="No job" /></SelectTrigger>
                <SelectContent className="bg-white max-h-72">
                  <SelectItem value={NONE}>No job</SelectItem>
                  {jobs.map((j) => <SelectItem key={j.id} value={j.id}>{j.code ? `${j.code} · ` : ""}{j.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea data-testid="po-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 rounded-md" />
            </div>
            <div>
              <Label>Amount</Label>
              <Input type="number" step="0.01" data-testid="po-amount-input" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1 rounded-md text-right tabular-nums" />
            </div>
            <div>
              <Label>PO Document (PDF)</Label>
              <div className="mt-1 flex items-center gap-3">
                <label className="inline-flex items-center gap-2 rounded-md border border-slate-200 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50" data-testid="po-upload-label">
                  <Upload className="h-4 w-4" /> {uploading ? "Uploading…" : "Upload PDF"}
                  <input type="file" accept=".pdf,.png,.jpg,.jpeg" className="hidden" data-testid="po-upload-input" onChange={onUpload} />
                </label>
                {form.document_path && (
                  <a href={fileUrl(form.document_path)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline">
                    <Paperclip className="h-3 w-3" /> View uploaded
                  </a>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-md" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="save-po-btn" onClick={save} className="rounded-md bg-slate-900 hover:bg-slate-800">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
